import { For, Show } from "solid-js";
import type { Component } from "solid-js";
import { useShellStore } from "@ui/state/shell";
import { useUserDetailQuery, updateUserField } from "../service.v2";
import { CollapsibleSection } from "@ui/components/CollapsibleSection";
import { InlineEditField } from "@ui/components/InlineEditField";
import { formatPhone } from "@utils/formatPhone";
import type { User } from "@core/services-v2";

const labelValue = (label: string, value: string | null | undefined) => (
  <div class="detail-field">
    <span class="detail-field__label">{label}</span>
    <span class="detail-field__value">{value ?? "-"}</span>
  </div>
);

const formatCurrency = (amount: number | null, currency: string | null): string => {
  if (amount === null || Number.isNaN(amount)) {
    return "-";
  }
  if (!currency) {
    return amount.toLocaleString();
  }
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(amount);
  } catch (_error) {
    return `${currency} ${amount}`;
  }
};

const formatSchedule = (scheduledDate: string | null, label: string | null): string => {
  if (scheduledDate && label) {
    return `${scheduledDate} - ${label}`;
  }
  if (scheduledDate) {
    return scheduledDate;
  }
  if (label) {
    return label;
  }
  return "Schedule pending";
};

const formatDateTime = (value: Date | null): string => (value ? value.toLocaleString() : "-");

export const UserDetailPanel: Component = () => {
  const { state } = useShellStore();
  const userId = () => state.selectedUserId;
  const query = useUserDetailQuery(userId);

  const detail = () => query.data ?? null;
  const user = () => detail()?.user ?? null;
  const bookings = () => detail()?.recentBookings ?? [];

  const isLoading = () => Boolean(query.isLoading);
  const error = () => (query.error instanceof Error ? query.error : query.error ? new Error(String(query.error)) : null);

  /**
   * Generic handler to update any user field
   */
  const handleUpdateUserField = async (
    fieldName: keyof Pick<User, 'displayName' | 'email' | 'phoneNumber' | 'photoURL'>,
    value: string
  ) => {
    const currentUserId = userId();
    if (!currentUserId) {
      throw new Error("No user ID available");
    }

    await updateUserField(currentUserId, fieldName, value);

    // Refetch the user detail data to get the updated value
    query.refetch();
  };

  return (
    <div class="chat-profile-panel">
      <Show when={userId()} fallback={<p>Select a customer to view their history.</p>}>
        <Show when={!isLoading()} fallback={<p>Loading customer details...</p>}>
          <Show
            when={!error()}
            fallback={
              <p class="empty-state">
                Unable to load customer details right now.
                <Show when={error()?.message}>{(message) => <span class="error-detail">{message()}</span>}</Show>
              </p>
            }
          >
            <Show when={user()} fallback={<p>User not found.</p>}>
              <CollapsibleSection title="PROFILE">
                <InlineEditField
                  value={user()?.displayName ?? null}
                  onSave={(value) => handleUpdateUserField('displayName', value)}
                  label="Name"
                  placeholder="Enter name..."
                />
                {labelValue("Email", user()?.email ?? null)}
                {labelValue("Phone", formatPhone(user()?.phoneNumber))}
                {labelValue("Joined", formatDateTime(user()?.createdAt ?? null))}
                {labelValue("Last active", formatDateTime(user()?.lastLoginAt ?? null))}
              </CollapsibleSection>

              <CollapsibleSection title="STATS">
                {labelValue(
                  "Completed cleanings",
                  user()?.stats?.totalCleanings != null ? String(user()?.stats?.totalCleanings) : "0",
                )}
                {labelValue(
                  "Square feet cleaned",
                  user()?.stats?.totalSquareFootCleaned != null
                    ? user()?.stats?.totalSquareFootCleaned?.toLocaleString()
                    : null,
                )}
              </CollapsibleSection>

              <CollapsibleSection title="RECENT BOOKINGS" defaultOpen={false}>
                <Show when={bookings().length > 0} fallback={<p class="empty-state">No bookings recorded yet.</p>}>
                  <ul class="profile-booking-list">
                    <For each={bookings()}>
                      {(booking) => (
                        <li class="profile-booking profile-booking--condensed">
                          <div class="profile-booking__line">
                            <span class={`booking-status booking-status--${booking.status}`}>{booking.status}</span>
                            <span class="profile-booking__amount">
                              {formatCurrency(booking.amount, booking.currency)}
                            </span>
                          </div>
                          <div class="profile-booking__line">
                            <span>{formatSchedule(booking.schedule.scheduledDate, booking.schedule.timeSlotLabel)}</span>
                          </div>
                          <div class="profile-booking__line profile-booking__line--muted">
                            <span>{formatDateTime(booking.createdAt)}</span>
                          </div>
                        </li>
                      )}
                    </For>
                  </ul>
                </Show>
              </CollapsibleSection>
            </Show>
          </Show>
        </Show>
      </Show>
    </div>
  );
};
